import React, { useEffect, useState } from "react";
import { fetchMe, logout, startGoogleLogin } from "../lib/auth";

/**
 * Federation Sign-In Badge — Emergent-managed Google Auth.
 *
 * Renders as a gold pill button on any page. When signed in, shows the
 * user's avatar + name and a sign-out control. When signed out, shows
 * "Sign in with Google".
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function LoginButton({ returnTo = "/university" }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // If we just returned from the OAuth flow, AuthCallback owns the
    // exchange — skip the /me probe so we don't race the cookie set.
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    (async () => {
      const me = await fetchMe();
      setUser(me);
      setChecking(false);
    })();
  }, []);

  const handleSignIn = () => startGoogleLogin(returnTo);

  const handleSignOut = async () => {
    await logout();
    setUser(null);
  };

  if (checking) {
    return (
      <span
        data-testid="auth-badge-loading"
        style={{
          display: "inline-flex",
          alignItems: "center",
          height: 40,
          padding: "0 1rem",
          color: "gold",
          border: "1px solid rgba(212,175,55,.35)",
          borderRadius: 999,
          fontFamily: "Georgia, serif",
          fontSize: 14,
          letterSpacing: ".08em",
        }}
      >
        …
      </span>
    );
  }

  if (user) {
    return (
      <span
        data-testid="auth-user-badge"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: ".6rem",
          height: 40,
          padding: "0 1rem",
          color: "#0a0a0f",
          background: "gold",
          border: "1px solid gold",
          borderRadius: 999,
          fontFamily: "Georgia, serif",
          fontSize: 14,
          whiteSpace: "nowrap",
        }}
      >
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            width={26}
            height={26}
            style={{ borderRadius: "50%", objectFit: "cover" }}
          />
        ) : null}
        <span data-testid="auth-user-name">{user.name || user.email}</span>
        <button
          type="button"
          onClick={handleSignOut}
          data-testid="auth-signout-btn"
          style={{
            marginLeft: ".4rem",
            background: "transparent",
            border: "1px solid #0a0a0f",
            color: "#0a0a0f",
            padding: "4px 10px",
            borderRadius: 999,
            fontFamily: "Georgia, serif",
            fontSize: 12,
            letterSpacing: ".08em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Sign out
        </button>
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={handleSignIn}
      data-testid="auth-signin-btn"
      aria-label="Sign in with Google"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: ".5rem",
        height: 40,
        padding: "0 .85rem",
        color: "gold",
        background: "transparent",
        border: "1px solid gold",
        borderRadius: 999,
        fontFamily: "Georgia, serif",
        fontSize: 13,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      <svg
        aria-hidden="true"
        width="18"
        height="18"
        viewBox="0 0 48 48"
        style={{ background: "white", borderRadius: "50%", padding: 1 }}
      >
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      </svg>
      <span>Sign in</span>
    </button>
  );
}
